import React, { useState } from 'react';
import { Spinner } from '@material-tailwind/react';
import { ClipboardCheckIcon, AlertTriangleIcon, CheckCircle2Icon, PlusIcon } from 'lucide-react';
import moment from 'moment';
import { usePermissions } from '../../../custom-hooks/use-permissions';
import { useGetInventoryPhysicalCounts } from '../../../custom-hooks/use-inventory';
import PhysicalCountModal from './PhysicalCountModal';

// Physical stock verification — someone counts what's actually on the shelf, the
// system diffs it against its own Remaining, and flags a mismatch for a manager to
// look into. Never auto-corrects Remaining (per SRD 3.3/3.4, simplified to one
// count instead of the old sheet's Check In/Check Out pair).
const PhysicalCountCard = ({ item }) => {
    const { can } = usePermissions();
    const [countModalOpen, setCountModalOpen] = useState(false);

    const { data: counts = [], isLoading } = useGetInventoryPhysicalCounts(item?._id);

    return (
        <div className="mb-5 rounded-2xl border border-inv-cream-200 bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <ClipboardCheckIcon size={18} className="text-inv-brand" />
                    <h3 className="font-bold text-inv-ink">Physical Count</h3>
                </div>
                {can('FX31', 'verify') && (
                    <button
                        onClick={() => setCountModalOpen(true)}
                        className="flex items-center gap-1.5 rounded-xl bg-inv-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-inv-brand-dark"
                    >
                        <PlusIcon size={16} /> Record Count
                    </button>
                )}
            </div>

            {isLoading ? (
                <div className="flex justify-center py-6"><Spinner className="h-5 w-5 text-inv-brand" /></div>
            ) : counts.length === 0 ? (
                <p className="text-sm text-[#8C8175]">No physical counts recorded yet.</p>
            ) : (
                <ul className="space-y-1.5">
                    {counts.slice(0, 8).map((c) => (
                        <li
                            key={c._id}
                            className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${c.mismatch ? 'bg-red-50' : 'bg-inv-cream-100'}`}
                        >
                            <div className="flex items-center gap-2">
                                {c.mismatch ? <AlertTriangleIcon size={14} className="text-red-600" /> : <CheckCircle2Icon size={14} className="text-emerald-600" />}
                                <div>
                                    <div className="text-inv-ink">
                                        Counted <span className="font-semibold">{c.countedQuantity}</span> vs system <span className="font-semibold">{c.systemRemainingAtCount}</span>
                                        {c.mismatch && <span className="ml-1.5 font-semibold text-red-600">({c.variance > 0 ? '+' : ''}{c.variance})</span>}
                                    </div>
                                    <div className="text-xs text-[#8C8175]">by {c.createdBy?.name || c.createdBy?.email || 'Unknown'}{c.note ? ` · ${c.note}` : ''}</div>
                                </div>
                            </div>
                            <span className="shrink-0 text-xs text-[#8C8175]">{moment(c.countedAt).format('DD MMM YYYY, h:mm A')}</span>
                        </li>
                    ))}
                </ul>
            )}

            <PhysicalCountModal isOpen={countModalOpen} onClose={() => setCountModalOpen(false)} item={item} />
        </div>
    );
};

export default PhysicalCountCard;
